import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Calendar, Mail, Settings, Users } from 'lucide-react';
import { FaHeart, FaEye } from 'react-icons/fa';
import { MdOutlineMenuBook } from 'react-icons/md';
import api from '../services/api';
import '../styles/Profile.css';
import getProfileImageUrl from '../utils/getProfileImageUrl';
import getBookCoverUrl from '../utils/getBookCoverUrl';

const Profile = () => {
    const { userId: profileUserId } = useParams();
    const navigate = useNavigate();
    const currentUserId = localStorage.getItem('userId');
    const token = localStorage.getItem('token');
    const [user, setUser] = useState(null);
    const [isFollowing, setIsFollowing] = useState(false);
    const [books, setBooks] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    // --- Data Fetching and State Logic (Unaltered) ---
    useEffect(() => {
        const fetchProfileData = async () => {
            if (!token) {
                navigate('/login');
                return;
            }
            try {
                if (!profileUserId) {
                    setError("Kullanıcı bulunamadı.");
                    setIsLoading(false);
                    return;
                }
                const response = await api.get(`/profile/${profileUserId}`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                const { user: userData, books: userBooks, isFollowing: followStatus } = response.data;
                const profilePhoto = getProfileImageUrl(userData.profile_image_url);

                setUser({
                    ...userData,
                    handle: `@${userData.username}`,
                    joinDate: new Date(userData.join_date).toLocaleDateString('tr-TR', {
                        year: 'numeric', month: 'long', day: 'numeric'
                    }),
                    profilePhoto
                });
                setBooks(userBooks.map(book => ({
                    id: book.book_id,
                    title: book.title,
                    cover: getBookCoverUrl(book.cover_image_url),
                    reads: book.total_views,
                    likes: book.total_likes,
                    publishedChapters: book.published_chapters_count,
                    author: userData.username
                })));
                setIsFollowing(followStatus);
            } catch (error) {
                console.error('Profil bilgileri alınırken hata:', error);
                setError('Profil bilgileri yüklenirken bir hata oluştu. Lütfen tekrar deneyin.');
                if (error.response?.status === 401) {
                    localStorage.removeItem('token');
                    localStorage.removeItem('userId');
                    navigate('/login');
                }
            } finally {
                setIsLoading(false);
            }
        };
        fetchProfileData();
        return () => {
            setUser(null);
            setBooks([]);
            setIsFollowing(false);
            setIsLoading(true);
        };
    }, [profileUserId, navigate, token]);

    const handleFollowClick = async () => {
        if (!token) {
            navigate('/login');
            return;
        }
        try {
            let response;
            if (isFollowing) {
                // Takipten çıkma isteği
                response = await api.post(`/profile/unfollow/${profileUserId}`, {}, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                // Backend'den gelen yanıtla state'i güncelle
                if (response.data.success) {
                    setUser(prevUser => ({
                        ...prevUser,
                        followers_count: response.data.followers_count
                    }));
                    setIsFollowing(false);
                }
            } else {
                // Takip etme isteği
                response = await api.post(`/profile/follow/${profileUserId}`, {}, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                // Backend'den gelen yanıtla state'i güncelle
                if (response.data.success) {
                     setUser(prevUser => ({
                        ...prevUser,
                        followers_count: response.data.followers_count
                    }));
                    setIsFollowing(true);
                }
            }
        } catch (error) {
            console.error('Takip durumu güncellenirken hata:', error);
            // Burada kullanıcıya bir hata mesajı gösterebilirsiniz.
        }
    };

    const handleMessageClick = () => navigate(`/inbox?recipient_username=${user.username}`);
    const handleCommunityClick = () => navigate(`/community/${profileUserId}`);
    const handleSettingsClick = () => navigate('/settings');

    // --- Render Logic ---
    if (isLoading) {
        return (
            <div className="profile-loading-container">
                <div className="profile-spinner"></div>
            </div>
        );
    }

    if (error) return <div className="profile-error-message">{error}</div>;
    if (!user) return <div className="profile-error-message">Kullanıcı bulunamadı</div>;

    const isOwnProfile = profileUserId === currentUserId;

    return (
        <div className="profile-page-modern">
            <header className="profile-header-modern">
                <div className="profile-cover-photo-modern"></div>
                <div className="profile-details-modern">
                    <div className="profile-avatar-container-modern">
                        {user.profilePhoto ? (
                            <img src={getProfileImageUrl(user.profilePhoto)} alt={user.username} className="profile-avatar-modern" />
                        ) : (
                            <div className="profile-avatar-placeholder-modern">
                                {user.username[0].toUpperCase()}
                            </div>
                        )}
                    </div>
                    <div className="profile-actions-modern">
                        <button onClick={handleMessageClick} className="profile-action-btn-modern secondary" title="Mesaj Gönder">
                            <Mail size={18} />
                        </button>
                        <button onClick={handleCommunityClick} className="profile-action-btn-modern secondary" title="Topluluk Sayfası">
                            <Users size={18} />
                        </button>

                        {isOwnProfile ? (
                            <button onClick={handleSettingsClick} className="profile-action-btn-modern tertiary">
                                <Settings size={18} /> Ayarları Düzenle
                            </button>
                        ) : (
                            <button onClick={handleFollowClick} className={`profile-action-btn-modern ${isFollowing ? 'secondary' : 'primary'}`}>
                                {isFollowing ? 'Takibi Bırak' : 'Takip Et'}
                            </button>
                        )}
                    </div>
                </div>
                <div className="profile-user-info-modern">
                    <h1 className="profile-display-name-modern">{user.full_name}</h1>
                    <p className="profile-username-modern">{user.handle}</p>
                    <p className="profile-bio-modern">{user.bio || 'Henüz bir biyografi eklenmemiş.'}</p>
                    <div className="profile-meta-modern">
                        <div className="profile-meta-item-modern">
                            <Calendar size={16} />
                            <span>{user.joinDate} tarihinde katıldı</span>
                        </div>
                    </div>
                    <div className="profile-stats-modern">
                        <div className="profile-stat-item-modern">
                            <strong>{user.following_count}</strong> Takip Edilen
                        </div>
                        <div className="profile-stat-item-modern">
                            <strong>{user.followers_count}</strong> Takipçi
                        </div>
                        <div className="profile-stat-item-modern">
                            <strong>{books.length}</strong> Kitap
                        </div>
                    </div>
                </div>
            </header>

            <main className="profile-content-modern">
                <div className="profile-books-section-modern">
                    <h2 className="profile-section-title-modern">{isOwnProfile ? "Hikayelerim" : `${user.username}'in Hikayeleri`}</h2>
                    {books.length > 0 ? (
                        <div className="profile-books-grid-modern">
                            {books.map(book => (
                                <Link to={`/book/${book.id}`} key={book.id} className="profile-book-card-modern">
                                    <div className="profile-book-cover-wrapper-modern">
                                        <img src={getBookCoverUrl(book.cover)} alt={book.title} className="profile-book-cover-modern" />
                                    </div>
                                    <div className="profile-book-info-modern">
                                        <h3 className="profile-book-title-modern">{book.title}</h3>
                                        <div className="profile-book-stats-modern">
                                            <span title="Okunma"><FaEye /> {book.reads}</span>
                                            <span title="Beğeni"><FaHeart /> {book.likes}</span>
                                            <span title="Bölüm"><MdOutlineMenuBook /> {book.publishedChapters}</span>
                                        </div>
                                    </div>
                                </Link>
                            ))}
                        </div>
                    ) : (
                        <div className="profile-no-books-message">
                            <p>Bu yazar henüz bir hikaye paylaşmadı.</p>
                        </div>
                    )}
                </div>
            </main>
        </div>
    );
};

export default Profile;